import { useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { createUseGesture, dragAction, pinchAction } from '@use-gesture/react';
import { Document, Page } from 'react-pdf';
import { useParams } from 'react-router-dom';

import { HSP_NAME_CODE } from 'app/config';
import { useBoundStore } from 'app/store';
import { useBlockScroll } from 'shared/model';

import { useGetConsentFile } from '../model/query';

const contentWidth = 595;

// 확대 / 축소가 정확하게 동작하지 않는 이슈가 있음.
// 확대 -> 이동 -> 축소 시 clamp에 걸리지 않고 화면을 벗어나는 현상이 있음.
// 더블탭 구현 전.
// QA 기간 ~12일 까지 제한적이라도 기능 구현이 들어가야 하며, 개발 어려운 부분이 있다면 기획과 상의 후 진행 필요.
export const SubmittedFormContainer = () => {
  const { contentCode } = useParams();
  const [numPages, setNumPages] = useState();
  const { hspId, usrId, hsptlzRsvNo } = useBoundStore((state) => state['hsptlzForm'].serviceParams);
  const { data } = useGetConsentFile({
    hspId,
    usrId,
    hsptlzRsvNo,
    confirmFileCode: contentCode,
  });

  const [{ x, y, scale }, api] = useSpring(() => ({
    scale: 1,
    x: 0,
    y: 0,
    config: { tension: 300, friction: 50 },
  }));

  const maxOffset = () => {
    const scaledWidth = contentWidth * scale.get();
    const scaledHeight = document.body.scrollHeight * scale.get();

    const viewWidth = window.innerWidth;
    const viewHeight = window.innerHeight;

    const tHidden = originXY.current.offsetY + originXY.current.y;
    const bHidden = scaledHeight - (tHidden + viewHeight);

    return {
      lMax: -Math.max(0, (scaledWidth - viewWidth) / 2),
      rMax: Math.max(0, (scaledWidth - viewWidth) / 2),
      tMax: Math.max(0, tHidden),
      bMax: Math.max(0, bHidden),
    };
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const useGesture = createUseGesture([dragAction, pinchAction]);
  const ref = useRef(null);
  const originXY = useRef({ x: 0, y: 0, offsetY: 0 });
  const lastTap = useRef(0);
  const wrapRef = useRef(null);
  const pinchedRef = useRef(false);
  const prevTranslateY = useRef();
  const memoRef = useRef({ tx: 0, ty: 0 });
  const dragRef = useRef({ x: 0, y: 0 });
  const getMaxOffsets = (curScale) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return { maxX: 0, maxY: 0 };

    const pdfWidth = rect.width;
    const pdfHeight = rect.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const maxX = Math.max(0, (pdfWidth * curScale - viewportWidth) / 2);
    const maxY = Math.max(0, (pdfHeight * curScale - viewportHeight) / 2);

    return { maxX, maxY };
  };
  const bind = useGesture(
    {
      onTouchStart: (e) => {
        // if (!pinchedRef.current || e.touches !== 1 || e.dragging) return;
        // const now = Date.now();
        // if (now - lastTap.current < 300) {
        //   api.start({
        //     scale: 1,
        //     x: 0,
        //     y: Number(prevTranslateY.current?.split('px')[0]) || 0,
        //     onRest: () => {
        //       pinchedRef.current = false;
        //       prevTranslateY.current = undefined;
        //     },
        //   });
        //   originXY.current = { y: 0, offsetY: 0 };
        // }
        // lastTap.current = now;
      },

      onPinchStart: ({ origin: [ox, oy] }) => {
        const rect = ref.current.getBoundingClientRect();
        originXY.current = {
          x: 0,
          y: -rect.top,
          offsetY: 0,
        };
      },

      onPinch: ({ offset: [d], origin: [ox, oy], memo, pinching }) => {
        if (!pinching) return;
        const nextScale = clamp(d / 100, 1, 3);
        const rect = ref.current.getBoundingClientRect();
        const { lMax, rMax, tMax, bMax } = maxOffset();

        if (scale.get() === 1) {
          pinchedRef.current = true;
          const tx = ox - (rect.left + rect.right / 2);
          const ty = oy - (rect.top + rect.bottom / 2);
          memoRef.current = { tx, ty };
        }

        let nextX = 0;
        let nextY = 0;

        // 축소 할 때
        if (nextScale < scale.get()) {
          const { maxX, maxY } = getMaxOffsets(nextScale);
          nextX = clamp(x.get() - memoRef.current.tx * (nextScale - scale.get()), -maxX, maxX);
          nextY = clamp(y.get() - memoRef.current.ty * (nextScale - scale.get()), -maxY, maxY);
        } else {
          // 확대 할 때

          if (x.get() > -lMax) nextX = x.get();
          else if (-x.get() > rMax) nextX = -rMax;
          else nextX = x.get() - memoRef.current.tx * (nextScale - scale.get());

          nextY =
            rect.top > 0 || oy - rect.top > rect.height
              ? y.get()
              : y.get() - memoRef.current.ty * (nextScale - scale.get());
        }

        api.start({
          scale: nextScale,
          x: nextX,
          y: nextY,
          immediate: true,
        });

        return memo;
      },

      onPinchEnd: ({ offset: [d], origin: [ox, oy], memo }) => {
        const rect = ref.current.getBoundingClientRect();
        pinchedRef.current = false;
        originXY.current = { x: rect.right, y: -rect.top, offsetY: y.get() };
      },

      onDrag: ({ first, movement: [mx, my], memo, dragging }) => {
        if (!dragging) return;
        if (pinchedRef.current) return;

        if (first) memo = [x.get(), y.get()];

        const [startX, startY] = memo ? memo : [x.get(), y.get()];
        const { lMax, rMax, tMax, bMax } = maxOffset();

        const nextX = clamp(startX + mx, lMax, rMax);
        const nextY = clamp(startY + my, -bMax, tMax);

        api.start({ x: nextX, y: nextY, immediate: true });
        return memo;
      },

      onDragEnd: ({ movement: [mx, my] }) => {
        dragRef.current = { x: mx, y: my };
      },
    },
    {
      // 설정 추가
      drag: {
        from: () => [x.get(), y.get()],
        filterTaps: true,
      },

      pinch: {
        from: () => [scale.get() * 100],
      },
    },
  );

  function onDocumentLoadSuccess({ numPages: nextNumPages }) {
    setNumPages(nextNumPages);
  }

  useBlockScroll();

  return (
    <>
      <div
        className='submitComplete-wrap btn-fixed'
        style={{
          touchAction: 'none',
          overflow: 'hidden',
          backgroundColor: numPages ? '#f5f5f5' : '#fff',
        }}
        ref={wrapRef}
      >
        <animated.div
          {...bind()}
          ref={ref}
          style={{
            x,
            y,
            scale,
            touchAction: 'none',
            display: 'inline-block',
            padding: '40px 0',
          }}
        >
          <Document
            file={
              data.consentFile
            }
            onLoadSuccess={onDocumentLoadSuccess}
            loading={null}
            error={null}
            noData={null}
            className='pdf-wrap'
          >
            {numPages &&
              Array.from(new Array(numPages), (_el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={505}
                  loading={null}
                  error={null}
                  noData={null}
                  className={`comp-template-wrap ${HSP_NAME_CODE[hspId]} ${contentCode}`}
                />
              ))}
          </Document>
        </animated.div>
      </div>
    </>
  );
};
